/**
 * **냉장고·주방 식품 소품** (2026-09-16, Chris: 「냉장고 안에 음식이나 물건도 다양하게 넣을 수 있게」).
 * 전부 블렌더 GLB(`tools/blender/food.py`) — 병의 목선·계란의 곡면은 코드 조각으로 안 나온다.
 * 아래 `build` 는 GLB 가 못 올 때의 대체이자 **크기 계약**이다(attachGlb 가 이 상자 높이에 모델을 맞춘다).
 *
 * 크기는 냉장고 선반 간격(0.26m)·내부 폭(0.50m) 안에 들어가게 잡았다. 조리대·상·선반 위에도 그대로 쓴다.
 */
import * as THREE from 'three';
import { bevelBox, lathe, type MeshWriter } from './kernel';
import type { Piece } from './pieces';

const G = (p: string) => `/models/sahol/${p}`;
/** 병 대체본 — 몸통 + 목 + 뚜껑 */
const bottle = (w: MeshWriter, r: number, h: number, neck: number, body: string, cap: string) => {
  w.part('병');
  const b = lathe([[0, 0], [r, 0.01], [r, h * 0.65], [r * 0.45, h * 0.8], [neck, h * 0.96], [0, h * 0.97]], 18);
  w.add(b, body as never, 0.4);
  w.part('뚜껑');
  const c = new THREE.CylinderGeometry(neck * 1.1, neck * 1.1, h * 0.1, 14);
  c.translate(0, h * 0.99, 0);
  w.add(c, cap as never, 0.3);
};

export const FOOD_PIECES: Piece[] = [
  {
    id: 'soju', name: '소주병', collection: 'sahol', category: '식품', nofront: true, glb: G('soju'),
    note: '초록 유리 소주병 한 병. 냉장고 문칸이나 상 위에.',
    openable: [{ part: '병뚜껑', node: 'soju_cap', slide: [0, 0.04, 0] }],
    dims: { w: 0.077, d: 0.077, h: 0.231 },
    build(w, d) { bottle(w, d.w / 2, d.h, 0.015, 'ebony', 'steel'); },
  },
  {
    id: 'makgeolli', name: '막걸리병', collection: 'sahol', category: '식품', nofront: true, glb: G('makgeolli'),
    note: '흰 플라스틱 막걸리 750ml. 초록 뚜껑에 누런 상표.',
    openable: [{ part: '뚜껑', node: 'mak_cap', slide: [0, 0.04, 0] }],
    dims: { w: 0.092, d: 0.091, h: 0.24 },
    build(w, d) { bottle(w, d.w / 2, d.h, 0.018, 'paper', 'painted'); },
  },
  {
    id: 'beer_can', name: '맥주캔', collection: 'sahol', category: '식품', nofront: true, glb: G('beer_can'),
    note: '500ml 캔. 따개 고리가 위에 남아 있다.',
    openable: [{ part: '따개', node: 'can_tab', slide: [0, 0.02, 0] }],
    dims: { w: 0.067, d: 0.067, h: 0.169 },
    build(w, d) {
      w.part('캔');
      const c = new THREE.CylinderGeometry(d.w / 2, d.w / 2, d.h, 20);
      c.translate(0, d.h / 2, 0);
      w.add(c, 'alu', 0.3);
    },
  },
  {
    id: 'milk_carton', name: '우유팩', collection: 'sahol', category: '식품', nofront: true, glb: G('milk_carton'),
    note: '1L 게이블탑 종이팩. 위가 지붕처럼 접히고 빨간 뚜껑이 붙어 있다.',
    openable: [{ part: '뚜껑', node: 'milk_cap', slide: [0, 0.04, 0] }],
    dims: { w: 0.072, d: 0.071, h: 0.209 },
    build(w, d) {
      w.part('팩');
      const b = bevelBox(d.w, d.h * 0.73, d.d, 0.004);
      b.translate(0, (d.h * 0.73) / 2, 0);
      w.add(b, 'cardboard', 0.5);
      w.part('지붕');
      const t = bevelBox(d.w, d.h * 0.27, d.d * 0.45, 0.004);
      t.translate(0, d.h * 0.73 + (d.h * 0.27) / 2, 0);
      w.add(t, 'cardboard', 0.5);
    },
  },
  {
    id: 'egg_tray', name: '계란판', collection: 'sahol', category: '식품', nofront: true, glb: G('egg_tray'),
    note: '10구 종이 계란판. 알이 가지런히 앉아 있다.',
    openable: [
      { part: '계란 1', node: 'egg00', slide: [0, 0.04, 0] },
      { part: '계란 2', node: 'egg41', slide: [0, 0.04, 0] },
    ],
    dims: { w: 0.266, d: 0.106, h: 0.057 },
    build(w, d) {
      w.part('판');
      const b = bevelBox(d.w, 0.032, d.d, 0.006);
      b.translate(0, 0.016, 0);
      w.add(b, 'cardboard', 0.6);
      w.part('알');
      for (let i = 0; i < 5; i++) for (let j = 0; j < 2; j++) {
        const e = lathe([[0, 0], [0.019, 0.012], [0.021, 0.024], [0.017, 0.038], [0, 0.044]], 12);
        e.translate(-0.108 + i * 0.054, 0.024, -0.027 + j * 0.054);
        w.add(e, 'paper', 0.4);
      }
    },
  },
  {
    id: 'kimchi_tub', name: '김치통', collection: 'sahol', category: '식품', nofront: true, glb: G('kimchi_tub'),
    note: '빨간 뚜껑 밀폐용기. 김치가 반쯤 담겨 있다.',
    openable: [{ part: '뚜껑', node: 'kim_lid', slide: [0, 0.05, 0], inside: { w: 0.18, h: 0.09, d: 0.13, at: [0, 0.05, 0] } }],
    dims: { w: 0.218, d: 0.167, h: 0.132 },
    build(w, d) {
      w.part('통');
      const b = bevelBox(d.w * 0.94, d.h * 0.88, d.d * 0.93, 0.012, 2);
      b.translate(0, (d.h * 0.88) / 2, 0);
      w.add(b, 'plastic', 0.4);
      w.part('뚜껑');
      const l = bevelBox(d.w, 0.018, d.d, 0.006, 2);
      l.translate(0, d.h * 0.88 + 0.009, 0);
      w.add(l, 'redcloth', 0.4);
    },
  },
  {
    id: 'banchan_set', name: '반찬통 셋', collection: 'sahol', category: '식품', nofront: true, glb: G('banchan_set'),
    note: '크기가 다른 밀폐용기 셋. 파란·녹색 뚜껑이 섞여 있다.',
    openable: [
      { part: '뚜껑 1', node: 'ctl0', slide: [0, 0.03, 0] },
      { part: '뚜껑 2', node: 'ctl1', slide: [0, 0.03, 0] },
      { part: '뚜껑 3', node: 'ctl2', slide: [0, 0.03, 0] },
    ],
    dims: { w: 0.252, d: 0.18, h: 0.055 },
    build(w) {
      w.part('통');
      for (const [x, z, s] of [[-0.07, 0, 1], [0.055, -0.01, 0.85], [0.05, 0.075, 0.72]] as const) {
        const b = bevelBox(0.115 * s, 0.055 * s, 0.085 * s, 0.008, 2);
        b.translate(x, (0.055 * s) / 2, z);
        w.add(b, 'plastic', 0.4);
        const l = bevelBox(0.123 * s, 0.01, 0.093 * s, 0.003, 2);
        l.translate(x, 0.055 * s + 0.005, z);
        w.add(l, 'fabric', 0.4);
      }
    },
  },
  {
    id: 'tofu_pack', name: '두부팩', collection: 'sahol', category: '식품', nofront: true, glb: G('tofu_pack'),
    note: '비닐 씌운 두부 한 모. 팩 안에 물이 조금 남아 있다.',
    openable: [
      { part: '비닐', node: 'tofu_film', slide: [0, 0.04, 0] },
      { part: '두부', node: 'tofu', slide: [0, 0.05, 0] },
    ],
    dims: { w: 0.149, d: 0.102, h: 0.046 },
    build(w, d) {
      w.part('팩');
      const b = bevelBox(d.w * 0.97, d.h * 0.96, d.d * 0.95, 0.008, 2);
      b.translate(0, (d.h * 0.96) / 2, 0);
      w.add(b, 'plastic', 0.3);
      w.part('두부');
      const t = bevelBox(d.w * 0.86, d.h * 0.8, d.d * 0.82, 0.004);
      t.translate(0, d.h * 0.45, 0);
      w.add(t, 'paper', 0.6);
    },
  },
  {
    id: 'yogurt_row', name: '요구르트 줄', collection: 'sahol', category: '식품', nofront: true, glb: G('yogurt_row'),
    note: '다섯 개가 붙은 채로 있는 요구르트. 노란 뚜껑.',
    openable: [
      { part: '요구르트', node: 'y2', slide: [0, 0.05, 0] },
      { part: '뚜껑', node: 'yf2', slide: [0, 0.02, 0] },
    ],
    dims: { w: 0.207, d: 0.039, h: 0.058 },
    build(w) {
      w.part('병');
      for (let i = 0; i < 5; i++) {
        const b = lathe([[0, 0], [0.0155, 0.002], [0.0165, 0.008], [0.019, 0.055], [0, 0.055]], 12);
        b.translate(-0.084 + i * 0.042, 0, 0);
        w.add(b, 'paper', 0.4);
      }
    },
  },
  {
    id: 'fruit_basket', name: '과일 바구니', collection: 'sahol', category: '식품', nofront: true, glb: G('fruit_basket'),
    note: '얕은 바구니에 사과 둘과 귤 셋. 냉장고 야채칸이나 상 위에.',
    openable: [
      { part: '사과', node: 'fr0', slide: [0, 0.05, 0] },
      { part: '귤', node: 'fr3', slide: [0, 0.05, 0] },
    ],
    dims: { w: 0.208, d: 0.206, h: 0.121 },
    build(w, d) {
      w.part('바구니');
      const b = lathe([[0.085, 0], [0.095, 0.006], [0.105, 0.055], [0.1, 0.058], [0.09, 0.008], [0.085, 0.004]], 20);
      w.add(b, 'paper', 0.6);
      w.part('과일');
      for (const [x, z, r] of [[-0.04, -0.02, 0.036], [0.035, -0.025, 0.034], [0, 0.04, 0.03], [-0.05, 0.045, 0.028], [0.055, 0.035, 0.027]] as const) {
        const f = new THREE.SphereGeometry(r, 12, 8);
        f.translate(x, 0.045 + r * 0.9, z);
        w.add(f, 'redcloth', 0.4);
      }
      void d;
    },
  },
];
